import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { KeyboardSensor, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { arrayMove, sortableKeyboardCoordinates } from "@dnd-kit/sortable";
import {
    buildInitialSelectorsFromStep,
    isReplayDeletableType,
    mapRowsToStepSelectors,
    mapSelectorTypeToStrategyType,
} from "./selectorsTabConstants";

/**
 * State and handlers for SelectorsTab — aligned with Replay `ConfigureXPath.jsx`:
 * add (pending row + Save/Cancel), edit on blur, delete, reorder.
 */
export function useSelectorsTab({ step, onStepChange, isExecuting = false, readOnlyForMetaInfo = false }) {
    const [isAddingCustom, setIsAddingCustom] = useState(false);
    const [customSelectorUid, setCustomSelectorUid] = useState(null);

    const controlsLocked = isExecuting || readOnlyForMetaInfo;

    const stepIdentity = step?.id ?? step?.uid ?? step?.stepId;
    const prevStepIdentityRef = useRef(stepIdentity);

    const initialSelectors = useMemo(() => buildInitialSelectorsFromStep(step), [step.id, step.uid, step.executionStrategies]);

    const [orderedSelectors, setOrderedSelectors] = useState(initialSelectors);
    const orderedSelectorsRef = useRef(orderedSelectors);
    useEffect(() => {
        orderedSelectorsRef.current = orderedSelectors;
    }, [orderedSelectors]);

    useEffect(() => {
        const stepChanged = prevStepIdentityRef.current !== stepIdentity;
        prevStepIdentityRef.current = stepIdentity;

        if (stepChanged) {
            setIsAddingCustom(false);
            setCustomSelectorUid(null);
            setOrderedSelectors(initialSelectors);
            return;
        }

        if (isAddingCustom) return;
        setOrderedSelectors(initialSelectors);
    }, [stepIdentity, initialSelectors, isAddingCustom]);

    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    const handleSelectorChange = useCallback((index, value) => {
        if (controlsLocked) return;
        setOrderedSelectors((prev) => {
            const updated = [...prev];
            if (!updated[index]) return prev;
            updated[index] = { ...updated[index], value };
            return updated;
        });
    }, [controlsLocked]);

    const handleSelectorBlur = useCallback(
        (index, value) => {
            if (controlsLocked) return;
            if (isAddingCustom) {
                setOrderedSelectors((prev) => {
                    const next = [...prev];
                    if (!next[index]) return prev;
                    next[index] = { ...next[index], value };
                    return next;
                });
                return;
            }

            setOrderedSelectors((prev) => {
                const current = prev[index];
                if (!current || current.isPending) return prev;

                const nextSelector = { ...current, value };
                const updated = prev.map((item, i) => (i === index ? nextSelector : item));

                const existingStrategies = Array.isArray(step.executionStrategies) ? step.executionStrategies : [];
                let newStrategies = existingStrategies;

                if (nextSelector.strategyId != null) {
                    newStrategies = existingStrategies.map((strategy) => {
                        if (String(strategy.id) !== String(nextSelector.strategyId)) return strategy;
                        return { ...strategy, selector: value, xpath: value };
                    });
                } else {
                    const newId = `selector-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
                    const newStrategy = {
                        id: newId,
                        type: mapSelectorTypeToStrategyType(nextSelector.type),
                        selector: value,
                        xpath: value,
                        priority: typeof nextSelector.priority === "number" ? nextSelector.priority : existingStrategies.length,
                        enabled: nextSelector.enabled !== false,
                    };
                    newStrategies = [...existingStrategies, newStrategy];
                    updated[index] = { ...nextSelector, strategyId: newId };
                }

                onStepChange({
                    selectors: mapRowsToStepSelectors(updated),
                    executionStrategies: newStrategies,
                });
                return updated;
            });
        },
        [controlsLocked, isAddingCustom, step.executionStrategies, onStepChange]
    );

    const handleDeleteSelector = useCallback(
        (index) => {
            if (controlsLocked) return;
            setOrderedSelectors((prev) => {
                const selectorToDelete = prev[index];
                if (!selectorToDelete || !isReplayDeletableType(selectorToDelete.type)) return prev;

                const updated = prev.filter((_, i) => i !== index);
                const stepPatch = { selectors: updated.length > 0 ? mapRowsToStepSelectors(updated) : undefined };

                if (selectorToDelete.strategyId != null && Array.isArray(step.executionStrategies)) {
                    stepPatch.executionStrategies = step.executionStrategies.filter(
                        (s) => String(s.id) !== String(selectorToDelete.strategyId)
                    );
                }

                onStepChange(stepPatch);
                return updated;
            });
        },
        [controlsLocked, step.executionStrategies, onStepChange]
    );

    const handleAddCustomSelector = useCallback(() => {
        if (controlsLocked) return;
        const uid = `pending-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
        setOrderedSelectors((prev) => [
            ...prev,
            {
                uid,
                strategyId: null,
                type: "xpath_custom",
                value: "",
                priority: prev.length,
                enabled: true,
                isPending: true,
            },
        ]);
        setIsAddingCustom(true);
        setCustomSelectorUid(uid);
    }, [controlsLocked]);

    const handleSaveCustomSelector = useCallback(() => {
        if (controlsLocked) return;
        if (!customSelectorUid) return;
        const prev = orderedSelectorsRef.current;
        const row = prev.find((s) => s.uid === customSelectorUid);
        const trimmed = row?.value?.trim();
        if (!trimmed) return;

        const newId = `xpath-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
        const existing = Array.isArray(step.executionStrategies) ? [...step.executionStrategies] : [];
        const priorityBase = Math.max(0, ...existing.map((s) => Number(s.priority) || 0), prev.length);

        const newStrategy = {
            id: newId,
            type: "xpath_custom",
            selector: trimmed,
            xpath: trimmed,
            priority: priorityBase + 1,
            enabled: true,
        };

        const updatedRows = prev.map((s) =>
            s.uid === customSelectorUid ? { ...s, strategyId: newId, value: trimmed, isPending: false } : s
        );

        onStepChange({
            executionStrategies: [...existing, newStrategy],
            selectors: mapRowsToStepSelectors(updatedRows),
        });

        setOrderedSelectors(updatedRows);
        setIsAddingCustom(false);
        setCustomSelectorUid(null);
    }, [controlsLocked, customSelectorUid, step.executionStrategies, onStepChange]);

    const handleCancelCustomSelector = useCallback(() => {
        if (!customSelectorUid) return;
        setOrderedSelectors((prev) => prev.filter((s) => s.uid !== customSelectorUid));
        setIsAddingCustom(false);
        setCustomSelectorUid(null);
    }, [customSelectorUid]);

    useEffect(() => {
        if (controlsLocked && isAddingCustom && customSelectorUid) {
            setOrderedSelectors((prev) => prev.filter((s) => s.uid !== customSelectorUid));
            setIsAddingCustom(false);
            setCustomSelectorUid(null);
        }
    }, [controlsLocked, isAddingCustom, customSelectorUid]);

    const handleDragEnd = useCallback(
        (event) => {
            if (controlsLocked) return;
            if (isAddingCustom) return;
            const { active, over } = event;
            if (!over || active.id === over.id) return;

            setOrderedSelectors((orderedSelectors) => {
                const oldIndex = orderedSelectors.findIndex((s) => String(s.uid) === String(active.id));
                const newIndex = orderedSelectors.findIndex((s) => String(s.uid) === String(over.id));
                if (oldIndex < 0 || newIndex < 0) return orderedSelectors;

                const reordered = arrayMove(orderedSelectors, oldIndex, newIndex).map((selector, idx) => ({
                    ...selector,
                    priority: idx,
                }));

                if (!Array.isArray(step.executionStrategies)) {
                    onStepChange({ selectors: mapRowsToStepSelectors(reordered) });
                    return reordered;
                }

                const updatedStrategies = step.executionStrategies.map((strategy) => {
                    const matching = reordered.find((s) => String(s.strategyId) === String(strategy.id));
                    if (!matching) return strategy;
                    return { ...strategy, priority: matching.priority };
                });

                onStepChange({
                    selectors: mapRowsToStepSelectors(reordered),
                    executionStrategies: updatedStrategies,
                });
                return reordered;
            });
        },
        [controlsLocked, isAddingCustom, step.executionStrategies, onStepChange]
    );

    const showList = orderedSelectors.length > 0;
    const dragDisabled = isAddingCustom || controlsLocked;

    const pendingRowHasValue = Boolean(orderedSelectors.find((s) => s.uid === customSelectorUid)?.value?.trim());

    return {
        sensors,
        orderedSelectors,
        isAddingCustom,
        customSelectorUid,
        handleSelectorChange,
        handleSelectorBlur,
        handleDeleteSelector,
        handleAddCustomSelector,
        handleSaveCustomSelector,
        handleCancelCustomSelector,
        handleDragEnd,
        showList,
        dragDisabled,
        pendingRowHasValue,
        hasExecutionStrategies: Array.isArray(step.executionStrategies) && step.executionStrategies.length > 0,
    };
}
